import { useEffect, useRef, useState } from "react";
import { App, Card, Input, Space, Typography } from "antd";
import { updateStageData } from "../../lib/api";
import type { WorkflowStage } from "../intakeDetails/types";

interface Props {
  workflowId: string;
  stage: WorkflowStage;
  /** Called after a successful save so the parent can refetch and re-evaluate
   *  the Advance button's enabled state. */
  onSaved?: () => void;
}

const SAVE_DEBOUNCE_MS = 300;

/**
 * AI Advisory Board notes — debounced auto-save textarea bound to
 * `workflow.stages[ai_advisory_board_presentation].notes`. Saves 300ms after
 * typing stops; advancing past this stage is gated on the notes being
 * non-empty (see `WorkflowTab.canAdvanceFromHere` + backend gating).
 */
export default function AdvisoryBoardNotes({ workflowId, stage, onSaved }: Props) {
  const { message } = App.useApp();
  const [value, setValue] = useState<string>(stage.notes ?? "");
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">(
    stage.notes ? "saved" : "idle",
  );
  const timerRef = useRef<number | null>(null);
  // Track the last value we successfully persisted so we don't fire redundant
  // saves on prop refresh (e.g., parent refetches after onSaved).
  const lastSavedRef = useRef<string>(stage.notes ?? "");

  // Re-sync from props when the parent refetches the workflow bundle. We only
  // accept upstream changes if they don't conflict with the user's pending
  // local edit (i.e., the textarea matches the last value we saved).
  useEffect(() => {
    const incoming = stage.notes ?? "";
    if (incoming !== lastSavedRef.current && value === lastSavedRef.current) {
      lastSavedRef.current = incoming;
      setValue(incoming);
    }
  }, [stage.notes, value]);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  const scheduleSave = (next: string) => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(async () => {
      if (next === lastSavedRef.current) {
        setStatus("saved");
        return;
      }
      try {
        setStatus("saving");
        await updateStageData(workflowId, stage.id, { notes: next });
        lastSavedRef.current = next;
        setStatus("saved");
        onSaved?.();
      } catch (error) {
        setStatus("error");
        if (error instanceof Error) message.error(error.message);
      }
    }, SAVE_DEBOUNCE_MS);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    setValue(next);
    setStatus("saving");
    scheduleSave(next);
  };

  return (
    <Card
      size="small"
      title="AI Advisory Board meeting notes"
      extra={
        <Typography.Text type="secondary" aria-live="polite">
          {status === "saving"
            ? "Saving…"
            : status === "saved"
              ? "Saved"
              : status === "error"
                ? "Save failed"
                : ""}
        </Typography.Text>
      }
    >
      <Space direction="vertical" size="small" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          Capture meeting outcomes, advisory board feedback, and follow-ups.
          Required to advance to Leadership Presentation.
        </Typography.Text>
        <Input.TextArea
          value={value}
          onChange={handleChange}
          autoSize={{ minRows: 4, maxRows: 12 }}
          placeholder="Write up the AI Advisory Board discussion, decisions, and next steps…"
          aria-label="AI Advisory Board meeting notes"
        />
      </Space>
    </Card>
  );
}
